import React, { useEffect, useState } from "react";
import { announceMedals } from "../medalEvents";
import Loading from "./Loading";
import Coin from "./Coin";
import { t, tx } from "../i18n";
import { useSounds } from "../sound";
import { PALETTES, THEMES } from "../theme";
import { ThemeMark, hasThemeMark } from "./ThemeLogo";
import { InitialsAvatar, bannerBackground } from "./PlayerIdentity";
import PageHead from "./PageHead";

const API = process.env.REACT_APP_API_URL || "";

// Ordre d'affichage des raretés ; « tbd » (à venir) passe après tout le reste.
const RARITY_RANK = { commun: 0, rare: 1, "épique": 2, "légendaire": 3, tbd: 4 };

// Illustration d'un objet. Les fichiers vivent dans public/sprite/shop/ ; tant
// qu'un dessin manque, le gabarit prend sa place — on peut donc remplir la
// boutique image par image sans jamais casser l'affichage.
const SHOP_PLACEHOLDER = "/sprite/shop/placeholder.svg";

function ItemImage({ src, size, className }) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      className={`shop-preview-img${failed ? " placeholder" : ""}${className ? " " + className : ""}`}
      src={failed ? SHOP_PLACEHOLDER : src}
      alt="" aria-hidden="true" draggable="false"
      style={{ width: size, height: size }}
      onError={() => setFailed(true)}
    />
  );
}

// Nom affiché : une palette ne dit rien sans son thème (« Nether » de quoi ?),
// on le préfixe donc du nom de l'habillage qu'elle recolore.
function itemLabel(item) {
  if (item.kind !== "palette") return item.name;
  const theme = THEMES.find(th => th.id === item.data?.themes?.[0]);
  return theme ? `${theme.name} · ${item.name}` : item.name;
}

// Item preview: each category is represented differently
export function ItemPreview({ item, size = 56 }) {
  const d = item.data || {};
  // Un habillage se présente par son propre lettrage — c'est ce qu'on achète,
  // bien plus parlant qu'une vignette générique. Ceux dont l'emblème est une
  // image (Popuwatch) la montrent, les autres leur lettrage dessiné.
  if (item.kind === "theme" && d.theme) {
    const logo = THEMES.find(th => th.id === d.theme)?.logo;
    if (logo) {
      return (
        <span class="thm-mark" data-mark-theme={d.theme}>
          <img class="thm-swatch-logo" src={logo} alt="" />
        </span>
      );
    }
    if (hasThemeMark(d.theme)) return <ThemeMark theme={d.theme} name={item.name} />;
  }
  // Avatar de thème : des initiales d'exemple, habillées comme le joueur les
  // portera. Bannière de thème : ses couches CSS, liseré compris.
  if (item.kind === "avatar" && d.look) return <InitialsAvatar name="Popu Quiz" size={size} look={d.look} />;
  if (item.kind === "banner" && d.css) {
    return <span className="shop-preview-banner" style={{ ...bannerBackground(d), boxShadow: d.ring }} />;
  }
  // Une illustration l'emporte sur tout le reste : c'est la forme finale.
  if (d.image) {
    // Les pictogrammes « envoyer sa propre image » gardent leur format carré.
    const wide = (item.kind === "banner" || item.kind === "theme") && !d.upload;
    return <ItemImage src={d.image} size={wide ? "100%" : size} className={wide ? "wide" : ""} />;
  }
  // Cadre de thème : le tour du jeton tel qu'il se portera, avec des initiales
  // d'exemple dedans. Style de pseudo : un nom d'exemple, dans sa parure.
  if (item.kind === "frame") {
    return (
      <span className={`shop-preview-frame${d.rainbow ? " rainbow" : ""}${d.glow ? " glow" : ""}${d.look ? ` framed fr-look fr-look-${d.look}` : ""}`}
        style={{ width: size, height: size, ...(d.color ? { borderColor: d.color } : {}) }}>
        {d.look && <InitialsAvatar name="Popu Quiz" size={size - 10} />}
      </span>
    );
  }
  if (item.kind === "color") {
    return d.look
      ? <span className={`shop-preview-name pid-name pn-look pn-look-${d.look}`}>Popu</span>
      : <span className="shop-preview-color" style={{ color: d.color }}>Aa</span>;
  }
  if (item.kind === "banner") {
    return <span className="shop-preview-banner" style={{ background: d.gradient }} />;
  }
  if (item.kind === "palette") {
    return (
      <span className="shop-preview-palette">
        {(d.swatch || []).map((c, i) => <span key={i} style={{ background: c }} />)}
      </span>
    );
  }
  return null;
}

// Petit badge « palette de peinture », posé sur une vignette de thème qui
// propose plusieurs jeux de couleurs — avec son propre texte au survol, pas
// le titre natif du navigateur : trop lent à apparaître, tronqué sur mobile.
function PaletteBadge() {
  const [open, setOpen] = useState(false);
  return (
    <span className="shop-palette-badge"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      tabIndex={0} role="note" aria-label={t("shop.paletteBadge")}>
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
        <path d="M12 3a9 9 0 0 0 0 18c1.3 0 2-.8 2-1.8 0-.5-.2-.9-.5-1.2-.3-.4-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8H16a5 5 0 0 0 5-5c0-3.9-4-7-9-7z"
          fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="7.5" cy="12" r="1.3" fill="currentColor" />
        <circle cx="9.8" cy="8.2" r="1.3" fill="currentColor" />
        <circle cx="14.2" cy="7.6" r="1.3" fill="currentColor" />
        <circle cx="17.4" cy="10.4" r="1.3" fill="currentColor" />
      </svg>
      {open && <span className="pq-tooltip">{t("shop.paletteBadge")}</span>}
    </span>
  );
}

function Shop({ account, onExpired, onBack }) {
  const { playSuccess, playFail } = useSounds();
  const [shop, setShop]   = useState(null);
  const [tab, setTab]     = useState("avatar");   // rayon ouvert à l'écran
  const [msg, setMsg]     = useState({ text: "", error: false });
  const [busy, setBusy]   = useState(null);
  const [drawn, setDrawn] = useState(null);   // last item drawn, for the reveal

  const auth = { Authorization: `Bearer ${account.token}` };

  const load = async () => {
    const r = await fetch(`${API}/shop`, { headers: auth });
    if (r.status === 401) return onExpired();
    setShop(await r.json());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (text, error = false) => {
    (error ? playFail : playSuccess)();
    setMsg({ text, error });
    setTimeout(() => setMsg({ text: "", error: false }), 3000);
  };

  const buy = async (item) => {
    setBusy(item.id);
    const r = await fetch(`${API}/shop/buy`, {
      method: "POST", headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ itemId: item.id }),
    });
    const d = await r.json().catch(() => ({}));
    setBusy(null);
    if (!r.ok) return flash(d.error || t("shop.buyFailed"), true);
    flash(t("shop.bought", { name: itemLabel(item) }));
    load();
  };

  const draw = async () => {
    setBusy("draw"); setDrawn(null);
    const r = await fetch(`${API}/shop/draw`, { method: "POST", headers: auth });
    const d = await r.json().catch(() => ({}));
    setBusy(null);
    if (!r.ok) return flash(d.error || t("shop.drawFailed"), true);
    playSuccess();
    setDrawn(d.item);
    announceMedals(d.medals);
    load();
  };

  const unlockSection = async (sec) => {
    setBusy("section:" + sec.id);
    const r = await fetch(`${API}/shop/unlock-section`, {
      method: "POST", headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ sectionId: sec.id }),
    });
    const d = await r.json().catch(() => ({}));
    setBusy(null);
    if (!r.ok) return flash(d.error || t("shop.unlockFailed"), true);
    flash(t("shop.sectionOpened", { name: sec.label }));
    load();
  };

  if (!shop) return <><PageHead onBack={onBack} title={t("shop.title")} /><div class="Shop"><Loading label={t("shop.loading")} /></div></>;

  const cur = shop.currency || { coin: t("shop.coin"), junk: t("shop.junk") };
  const sections = shop.sections || [];
  const section = sections.find(sc => sc.id === tab) || sections[0];
  const visible = section ? shop.items.filter(i => section.kinds.includes(i.kind)) : [];
  // Le rayon Thèmes mélange deux natures (palette, thème) : on le groupe par
  // nature plutôt que par monnaie, comme les autres rayons groupent junk/or —
  // même mécanique d'affichage, juste un autre critère de coupure.
  const byKind = section?.kinds.includes("palette") && section?.kinds.includes("theme");
  // Dans chaque groupe, du plus courant au plus rare : les légendaires ferment
  // la marche.
  const byRarity = (a, b) => (RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9);
  const group1Items = (byKind ? visible.filter(i => i.kind === "palette") : visible.filter(i => i.currency === "junk")).sort(byRarity);
  const group2Items = (byKind ? visible.filter(i => i.kind === "theme") : visible.filter(i => i.currency !== "junk")).sort(byRarity);
  const group1Label = byKind ? t("shop.groupPalette") : tx("shop.groupJunk", { coin: <Coin kind="junk" size={17} /> });
  const group2Label = byKind ? t("shop.groupTheme") : tx("shop.groupCoin", { coin: <Coin size={17} /> });
  const ordered = [...group1Items, ...group2Items];

  return (
    <>
    <PageHead onBack={onBack} title={t("shop.title")}>
      <div class="shop-wallet">
        <span class="shop-balance" title={cur.coin}><Coin size={22} /> {shop.wallet.coins}</span>
        <span class="shop-balance junk" title={cur.junk}><Coin kind="junk" size={22} /> {shop.wallet.junk}</span>
      </div>
    </PageHead>
    <div class="Shop">

      {msg.text && <div class={`shop-msg${msg.error ? " error" : ""}`}>{msg.text}</div>}

      {shop.draw && (
        <div class="shop-draw">
          {/* Médaillon « mystère » : le tirage n'a pas besoin d'un emoji cadeau
              pour se faire comprendre. */}
          <div class="shop-draw-medal" aria-hidden="true"><span>?</span></div>
          <div class="shop-draw-info">
            <span class="shop-draw-title">{t("shop.drawTitle")}</span>
            <span class="shop-draw-sub">
              {shop.draw.remaining === 0
                ? t("shop.drawOwnAll")
                : shop.draw.usedToday === 0
                ? tx("shop.drawFirst", { price: <b class="shop-draw-price"><Coin size={15} />{shop.draw.firstPrice}</b> })
                : tx("shop.drawNext", { price: <b class="shop-draw-price"><Coin size={15} />{shop.draw.nextPrice}</b>, remaining: shop.draw.remaining })}
            </span>
            <span class="shop-draw-odds">
              {Object.entries(shop.draw.odds).map(([r, pct]) => (
                <span key={r} class={`pq-tag shop-odds rarity-${r}`}>{r}<em>{pct}%</em></span>
              ))}
            </span>
          </div>
          <button class="btn btn-primary shop-draw-btn"
            disabled={busy === "draw" || shop.draw.remaining === 0 || shop.wallet.coins < shop.draw.price}
            onClick={draw}>
            {busy === "draw"
              ? <span class="shop-draw-wait" />
              : <>{t("shop.drawButton")} <span class="shop-draw-cost"><Coin size={19} />{shop.draw.price}</span></>}
          </button>
        </div>
      )}

      {drawn && (
        <div class={`shop-drawn rarity-${drawn.rarity}`} onClick={() => setDrawn(null)}>
          <span class="shop-drawn-label">{t("shop.drawnLabel")}</span>
          <div class="shop-drawn-preview"><ItemPreview item={drawn} size={72} /></div>
          <span class="shop-drawn-name">{itemLabel(drawn)}</span>
          <span class="shop-drawn-rarity">{drawn.rarity}</span>
        </div>
      )}

      <div class="shop-tabs">
        {sections.map(sc => (
          <button key={sc.id}
            class={`shop-tab${tab === sc.id ? " active" : ""}${!sc.open ? " closed" : ""}`}
            onClick={() => setTab(sc.id)}>
            {sc.label}
          </button>
        ))}
      </div>

      {/* Rayon fermé : on montre ce qu'il contient sans donner accès au contenu. */}
      {section && !section.open && (
        <div class={`shop-section-lock${section.comingSoon ? " soon" : ""}`}>
          <span class="shop-section-lock-title">
            {section.comingSoon ? t("shop.sectionSoon") : t("shop.sectionClosed", { name: section.label })}
          </span>
          <p class="shop-section-lock-text">
            {section.comingSoon ? t("shop.sectionSoonText") : t("shop.sectionClosedText")}
          </p>
          {!section.comingSoon && (
            <button class="btn btn-primary" disabled={busy === "section:" + section.id || shop.wallet.coins < section.unlockPrice}
              onClick={() => unlockSection(section)}>
              {t("shop.sectionUnlock")} <span class="shop-price"><Coin size={19} />{section.unlockPrice}</span>
            </button>
          )}
        </div>
      )}

      <div class={`shop-grid${section && !section.open ? " preview-only" : ""}`}>
        {/* La monnaie de consolation ouvre le rayon : c'est ce qu'on peut
            s'offrir en perdant, autant le voir en premier. Pour un rayon
            multi-nature (Thèmes), même mécanique mais coupée par nature. */}
        {group1Items.length > 0 && group2Items.length > 0 && (
          <p class="shop-group-title">{group1Label}</p>
        )}
        {ordered.map((item, i) => {
          const separate = group1Items.length > 0 && group2Items.length > 0 && i === group1Items.length;
          const price = <span class="shop-price"><Coin kind={item.currency === "junk" ? "junk" : "coin"} size={19} />{item.price}</span>;
          const canAfford = (item.currency === "junk" ? shop.wallet.junk : shop.wallet.coins) >= item.price;
          return (
            <React.Fragment key={item.id}>
            {separate && <p class="shop-group-title">{group2Label}</p>}
            <div class={`shop-item rarity-${item.comingSoon ? "tbd" : item.rarity}${item.owned ? " owned" : ""}${item.locked && !item.owned ? " locked" : ""}`}>
              <div class="shop-item-preview">
                <ItemPreview item={item} />
                {item.kind === "theme" && PALETTES[item.data?.theme]?.length > 1 && <PaletteBadge />}
              </div>
              <span class="shop-item-name">{itemLabel(item)}</span>
              <span class="shop-item-rarity">{item.comingSoon ? t("shop.soon") : item.rarity}</span>
              {item.comingSoon ? (
                <span class="shop-item-locked">{t("shop.itemSoon")}</span>
              ) : section && !section.open ? (
                // Le bandeau du rayon dit déjà qu'il est fermé : l'article
                // montre son prix, grisé, pour qu'on sache ce qu'il coûtera.
                <button class="btn btn-primary shop-btn" disabled title={t("shop.itemSectionClosed")}>{price}</button>
              ) : item.owned ? (
                // Ce qu'on possède ne se porte pas d'ici : avatars et bannières
                // se choisissent sur le profil, habillages et palettes dans les
                // réglages. L'article reste en vitrine, grisé.
                <span class="shop-item-locked">{t("shop.owned")}</span>
              ) : item.locked ? (
                <span class="shop-item-locked">🔒 {item.unlockLabel}</span>
              ) : item.auto ? (
                <span class="shop-item-locked">🔒 {item.unlockLabel}</span>
              ) : (
                <>
                  {/* Les jokers se cumulent : on montre combien on en a déjà */}
                  {item.kind === "joker" && (
                    <span class="shop-item-locked">{t("shop.jokerQty", { n: item.qty || 0 })}</span>
                  )}
                  <button class="btn btn-primary shop-btn" disabled={busy === item.id || !canAfford}
                    onClick={() => buy(item)}>
                    {price}
                  </button>
                </>
              )}
            </div>
            </React.Fragment>
          );
        })}
        {visible.length === 0 && <p class="shop-empty">{t("shop.empty")}</p>}
      </div>

    </div>
    </>
  );
}

export default Shop;
